import { Component, Inject, OnDestroy, OnInit, Optional, ViewEncapsulation } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgbModal, NgbPagination, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { ClientFullModel, ComponentType, HX_COMPONENT_NAME, HxClientService, toRouterQueryParams } from 'hx-services';
import { HxClientEditModal } from '../client/edit/client-edit-modal/client-edit.modal';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { HxLoaderComponent } from '../loader/hx-loader.component';
import { TranslocoPipe } from '@jsverse/transloco';
@Component({
    selector: 'hx-client-list.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './client-list.component.html',
    styleUrls: ['./client-list.component.css'],
    encapsulation: ViewEncapsulation.None,
    imports: [FormsModule, NgIf, NgFor, NgClass, HxLoaderComponent, NgbTooltip, NgbPagination, DatePipe, TranslocoPipe]
})
export class HxClientListComponent implements OnInit, OnDestroy {
  list: ClientFullModel[] = [];
  pagination = {allItemCount: 0};
  loadFailed = false;
  errorId?: string;
  searchDraft = '';
  editId?: number;
  isLoading = {
    list: false,
    edit: false
  };
  isCallcenter: boolean;
  params: {
    page: number;
    limit: number;
    query?: string;
    birthDay?: number;
    birthMonth?: number;
  } = {page: 1, limit: 20};
  days: number[] = Array.from({length: 31}, (_, i) => i + 1);
  months: number[] = Array.from({length: 12}, (_, i) => i + 1);
  pageSizes = [20, 50, 100];
  private sub!: Subscription;
  private loadRequestId = 0;
  private lastQueryParams = '';

  constructor(
    private modal: NgbModal,
    private clientService: HxClientService,
    private aRoute: ActivatedRoute,
    private router: Router,
    @Optional() @Inject(HX_COMPONENT_NAME) private componentName: string,
  ) {
    this.isCallcenter = this.componentName === ComponentType.cc;
  }

  ngOnInit() {
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      this.params.page = this.numberParam(paramMap, 'page', 1, 1000000) ?? 1;
      this.params.limit = this.numberParam(paramMap, 'limit', 20, 1000) ?? 20;
      this.params.query = paramMap.get('query') ?? '';
      this.searchDraft = this.params.query;
      this.params.birthDay = this.numberParam(paramMap, 'birthDay', 1, 31);
      this.params.birthMonth = this.numberParam(paramMap, 'birthMonth', 1, 12);
      this.lastQueryParams = JSON.stringify(toRouterQueryParams(this.params));
      this.loadList();
    });
  }

  addItem() {
    this.modal.open(HxClientEditModal, {size: 'lg'}).result.then(() => {
      this.loadList();
    }, (reason) => {
      console.log('Dismissed reason : ', reason);
    });
  }

  updateItem(client: ClientFullModel) {
    const modalInstance = this.modal.open(HxClientEditModal, {size: 'lg'});
    modalInstance.componentInstance.id = client.client.id;
    modalInstance.result.then(() => {
      this.loadList();
    }, (reason) => {
      console.log('Dismissed reason : ', reason);
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.loadRequestId++;
  }

  resetFilter(): void {
    this.params = {page: 1, limit: 20};
    this.searchDraft = '';
    this.pageChanged(1);
  }

  onSubmitFilter(): void {
    this.params.query = this.searchDraft.trim() || undefined;
    this.pageChanged(1);
  }

  onLimitChanged(): void {
    this.pageChanged(1);
  }

  get hasActiveFilters(): boolean {
    return !!(this.params.query || this.params.birthDay || this.params.birthMonth);
  }

  initials(name?: string): string {
    return (name || '').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase() || '—';
  }

  async loadList() {
    const requestId = ++this.loadRequestId;
    this.isLoading.list = true;
    this.loadFailed = false;
    this.errorId = undefined;
    this.list = [];
    this.pagination.allItemCount = 0;
    try {
      const result = await this.clientService.getClientList({...this.params});
      if (requestId !== this.loadRequestId) {
        return;
      }
      this.list = result.list || [];
      this.pagination.allItemCount = result.count;
    } catch (error: unknown) {
      if (requestId !== this.loadRequestId) {
        return;
      }
      this.loadFailed = true;
      if (error instanceof HttpErrorResponse && typeof error.error?.id === 'string') {
        this.errorId = error.error.id;
      }
    } finally {
      if (requestId === this.loadRequestId) {
        this.isLoading.list = false;
      }
    }
  }

  pageChanged(page: number) {
    this.params.page = page;
    this.navigateUrl();
  }

  applyFilters() {
    if (this.params.birthDay == null) {
      this.params.birthDay = undefined;
    }
    if (this.params.birthMonth == null) {
      this.params.birthMonth = undefined;
    }
    this.pageChanged(1);
  }

  private navigateUrl() {
    const queryParams = toRouterQueryParams(this.params);
    if (JSON.stringify(queryParams) === this.lastQueryParams) {
      this.loadList();
      return;
    }
    this.router.navigate(['./'], {
      queryParams,
      relativeTo: this.aRoute
    }).then(navigated => {
      // The router does not emit queryParamMap again for an unchanged filter.
      if (!navigated) {
        this.loadList();
      }
    });
  }

  private numberParam(params: ParamMap, name: string, min: number, max: number): number | undefined {
    const value = params.get(name);
    if (!value || !/^\d+$/.test(value)) {
      return undefined;
    }
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= min && number <= max ? number : undefined;
  }
}
